import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PERMISSION_MODULES as PM } from '../constants/permissionModules';
import Layout from '../components/Layout';
import AthleteAvatar from '../components/AthleteAvatar';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { emptyForm, buildStatsDraft, buildStatsPayload } from '../components/matchperf/constants';
import { TagPicker } from '../components/matchperf/shared';
import AthletePerformanceRail from '../components/matchperf/AthletePerformanceRail';
import PerformanceOverview from '../components/matchperf/PerformanceOverview';
import QuickAnalysisForm from '../components/matchperf/QuickAnalysisForm';
import ObservationTimeline from '../components/matchperf/ObservationTimeline';
import PerformanceStatistics from '../components/matchperf/PerformanceStatistics';
import { VideoEvidencePanel } from '../components/matchperf/VideoEvidencePanel';
import { MatchPhysicalGPSPanel } from '../components/matchperf/MatchGpsPanel';
import EditObservationDialog from '../components/matchperf/EditObservationDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MatchPerformancePage = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const { hasModuleAccess } = useAuth();

  const canView = hasModuleAccess(PM.MATCHES, 'view');
  const canEdit = hasModuleAccess(PM.MATCHES, 'edit');
  const canDelete = hasModuleAccess(PM.MATCHES, 'delete');
  const canManageEvidence = hasModuleAccess(PM.MATCHES_MANAGE_EVIDENCE, 'edit');

  const [loading, setLoading] = useState(true);
  const [match, setMatch] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [athletesById, setAthletesById] = useState({});
  const [selectedId, setSelectedId] = useState(sp.get('athlete') || '');
  const [tab, setTab] = useState('analysis');

  const [tags, setTags] = useState([]);
  const tagsById = useMemo(() => Object.fromEntries(tags.map(t => [t.id, t])), [tags]);

  const [perf, setPerf] = useState(null);
  const [derived, setDerived] = useState({});
  const [observations, setObservations] = useState([]);
  const [obsFilter, setObsFilter] = useState('all');
  const [evaluation, setEvaluation] = useState(null);

  const [form, setForm] = useState(emptyForm());
  const [tagPickerOpen, setTagPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editObs, setEditObs] = useState(null);
  const [statsDraft, setStatsDraft] = useState({});
  const [statsDirty, setStatsDirty] = useState(false);
  const [statsSaving, setStatsSaving] = useState(false);
  const tsRef = useRef(null);

  const loadMatch = useCallback(async () => {
    setLoading(true);
    try {
      const [detail, ath, tagResp] = await Promise.all([
        axios.get(`${API}/matches-v3/${matchId}`),
        axios.get(`${API}/athletes`),
        axios.get(`${API}/development-tags`),
      ]);
      setMatch(detail.data.match);
      const parts = detail.data.participants || [];
      setParticipants(parts);
      const list = Array.isArray(ath.data) ? ath.data : (ath.data?.athletes || []);
      setAthletesById(Object.fromEntries(list.map(a => [a.id, a])));
      setTags(tagResp.data.tags || []);
      setSelectedId(prev => prev || (parts[0]?.athlete_id || ''));
    } catch (e) {
      toast.error('Failed to load match');
    } finally { setLoading(false); }
  }, [matchId]);

  useEffect(() => { loadMatch(); }, [loadMatch]);

  const selectedParticipant = useMemo(
    () => participants.find(p => p.athlete_id === selectedId) || null,
    [participants, selectedId]);

  const loadAthleteData = useCallback(async () => {
    if (!selectedId) return;
    try {
      const [pf, obs] = await Promise.all([
        axios.get(`${API}/matches-v3/${matchId}/athletes/${selectedId}/performance`),
        axios.get(`${API}/matches-v3/${matchId}/observations?athlete_id=${selectedId}`),
      ]);
      setPerf(pf.data.performance);
      setDerived(pf.data.derived || {});
      setObservations(obs.data.observations || []);
      setStatsDraft(buildStatsDraft(pf.data.performance));
      setStatsDirty(false);
      const part = participants.find(p => p.athlete_id === selectedId);
      if (part) {
        try {
          const ev = await axios.get(`${API}/match-reports-v2/evaluations/report/${part.id}`);
          setEvaluation(ev.data || null);
        } catch { setEvaluation(null); }
      } else { setEvaluation(null); }
    } catch (e) {
      if (e.response?.status === 403) toast.error('You do not have access to this performance data');
      else toast.error('Failed to load athlete performance');
    }
  }, [matchId, selectedId, participants]);

  useEffect(() => { loadAthleteData(); }, [loadAthleteData]);

  const switchAthlete = (aid) => {
    if (statsDirty && !window.confirm('You have unsaved statistics. Discard and switch athlete?')) return;
    setSelectedId(aid);
    setForm(emptyForm());
    setObsFilter('all');
    const next = new URLSearchParams(sp); next.set('athlete', aid); setSp(next, { replace: true });
  };

  const toggleTag = (id) => setForm(f => ({
    ...f,
    development_tag_ids: f.development_tag_ids.includes(id)
      ? f.development_tag_ids.filter(x => x !== id)
      : [...f.development_tag_ids, id],
  }));

  const buildObsPayload = (f) => ({
    athlete_id: selectedId,
    period: f.period || null,
    timestamp: f.timestamp || null,
    category: f.category,
    development_tag_ids: f.development_tag_ids,
    assessment: f.assessment,
    observation: f.observation || null,
    evidence: f.evidence
      .filter(e => e.source && (e.timestamp || e.url || e.clip_url || e.label))
      .map(e => ({ source: e.source, timestamp: e.timestamp || null, url: e.url || null,
                   clip_url: e.clip_url || null, label: e.label || null })),
  });

  const saveObservation = async (addAnother) => {
    if (!form.category) { toast.error('Pick a category'); return; }
    setSaving(true);
    try {
      const resp = await axios.post(`${API}/matches-v3/${matchId}/observations`, buildObsPayload(form));
      setObservations(prev => [...prev, resp.data].sort(
        (a, b) => (a.timestamp_seconds ?? 1e9) - (b.timestamp_seconds ?? 1e9)));
      toast.success('Observation saved');
      if (addAnother) {
        setForm(f => ({ ...emptyForm(), period: f.period }));
        setTimeout(() => tsRef.current?.focus(), 50);
      } else {
        setForm(emptyForm());
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save observation');
    } finally { setSaving(false); }
  };

  const saveEditObs = async () => {
    if (!editObs) return;
    setSaving(true);
    try {
      await axios.put(`${API}/matches-v3/observations/${editObs.id}`, {
        period: editObs.period || null,
        timestamp: editObs.timestamp_display || null,
        category: editObs.category,
        development_tag_ids: editObs.development_tag_ids,
        assessment: editObs.assessment,
        observation: editObs.observation || null,
        evidence: (editObs.evidence || []).map(e => ({
          id: e.id, source: e.source, timestamp_seconds: e.timestamp_seconds,
          timestamp: e.timestamp_display, url: e.url, clip_url: e.clip_url,
          label: e.label, provider_ref: e.provider_ref,
        })),
      });
      toast.success('Observation updated');
      setEditObs(null);
      loadAthleteData();
    } catch (e) {
      if (e.response?.status === 403) toast.error(e.response?.data?.detail || 'Evidence management requires additional permission');
      else toast.error(e.response?.data?.detail || 'Failed to update');
    } finally { setSaving(false); }
  };

  const deleteObs = async (obs) => {
    if (!window.confirm('Delete this observation? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/matches-v3/observations/${obs.id}`);
      setObservations(prev => prev.filter(o => o.id !== obs.id));
      toast.success('Observation deleted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const saveStats = async () => {
    setStatsSaving(true);
    try {
      const payload = buildStatsPayload(statsDraft, perf);
      const resp = await axios.put(`${API}/matches-v3/${matchId}/athletes/${selectedId}/performance`, payload);
      setPerf(resp.data.performance);
      setDerived(resp.data.derived || {});
      setStatsDraft(buildStatsDraft(resp.data.performance));
      setStatsDirty(false);
      toast.success('Statistics saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save statistics');
    } finally { setStatsSaving(false); }
  };

  const filteredObs = useMemo(() => obsFilter === 'all'
    ? observations
    : observations.filter(o => o.assessment === obsFilter), [observations, obsFilter]);

  const allEvidence = useMemo(() => observations.flatMap(o =>
    (o.evidence || []).map(e => ({ ...e, _obs: o }))), [observations]);

  if (!canView) {
    return (
      <Layout>
        <div className="p-8 text-center text-gray-500" data-testid="mp-no-access">
          You do not have access to Individual Match Performance.
        </div>
      </Layout>
    );
  }
  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 lg:p-6 space-y-4" data-testid="match-performance-page">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/matches/${matchId}`)} data-testid="mp-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to match
          </Button>
          <div className="text-sm text-gray-500" data-testid="mp-match-context">
            {match ? `vs ${match.opponent} · ${match.match_date} · ${match.competition}` : ''}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[16rem_1fr] gap-4">
          <AthletePerformanceRail
            participants={participants} athletesById={athletesById}
            selectedId={selectedId} onSelect={switchAthlete} />

          <div className="min-w-0">
            {!selectedParticipant ? (
              <Card><CardContent className="py-16 text-center text-gray-500" data-testid="mp-pick-athlete">
                Select a participant to begin analysis.
              </CardContent></Card>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-3" data-testid="mp-selected-header">
                  <AthleteAvatar
                    photoUrl={athletesById[selectedId]?.profile_photo}
                    thumbUrl={athletesById[selectedId]?.profile_photo_thumbnail}
                    name={selectedParticipant.athlete_name} size="md" showName
                    subline={`${selectedParticipant.position || '—'} · ${selectedParticipant.minutes_played ?? '—'} min`} />
                </div>

                <Tabs value={tab} onValueChange={setTab}>
                  <TabsList className="flex flex-wrap h-auto" data-testid="mp-tabs">
                    <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
                    <TabsTrigger value="analysis" data-testid="tab-analysis">Analysis</TabsTrigger>
                    <TabsTrigger value="statistics" data-testid="tab-statistics">Statistics</TabsTrigger>
                    <TabsTrigger value="evidence" data-testid="tab-evidence">Video Evidence</TabsTrigger>
                    <TabsTrigger value="gps" data-testid="tab-gps">Physical / GPS</TabsTrigger>
                  </TabsList>

                  <TabsContent value="overview" className="mt-4">
                    <PerformanceOverview participant={selectedParticipant} evaluation={evaluation} />
                  </TabsContent>

                  <TabsContent value="analysis" className="mt-4">
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                      {canEdit && (
                        <QuickAnalysisForm
                          form={form} setForm={setForm} tagsById={tagsById} tsRef={tsRef}
                          saving={saving} onOpenTags={() => setTagPickerOpen(true)}
                          onToggleTag={toggleTag} onSave={saveObservation} />
                      )}
                      <ObservationTimeline
                        observations={observations} filtered={filteredObs}
                        obsFilter={obsFilter} setObsFilter={setObsFilter} tagsById={tagsById}
                        canEdit={canEdit} canDelete={canDelete} onEdit={setEditObs} onDelete={deleteObs} />
                    </div>
                  </TabsContent>

                  <TabsContent value="statistics" className="mt-4">
                    <PerformanceStatistics
                      statsDraft={statsDraft} setStatsDraft={setStatsDraft} setStatsDirty={setStatsDirty}
                      statsDirty={statsDirty} derived={derived} perf={perf} canEdit={canEdit}
                      statsSaving={statsSaving} onSave={saveStats} />
                  </TabsContent>

                  <TabsContent value="evidence" className="mt-4">
                    <VideoEvidencePanel allEvidence={allEvidence} tagsById={tagsById} />
                  </TabsContent>

                  <TabsContent value="gps" className="mt-4">
                    <MatchPhysicalGPSPanel
                      perf={perf} matchId={matchId} athleteId={selectedId}
                      activityDate={match?.match_date} canEdit={canEdit}
                      onSaved={loadAthleteData} />
                  </TabsContent>
                </Tabs>
              </>
            )}
          </div>
        </div>
      </div>

      <TagPicker open={tagPickerOpen} onOpenChange={setTagPickerOpen} tags={tags}
                 selected={form.development_tag_ids} onToggle={toggleTag} />

      <EditObservationDialog
        editObs={editObs} setEditObs={setEditObs} tags={tags} tagsById={tagsById}
        canManageEvidence={canManageEvidence} saving={saving} onSave={saveEditObs} />
    </Layout>
  );
};

export default MatchPerformancePage;
